import { Fragment, ReactNode, Suspense, useCallback } from "react";
import { IGif, INicheFeedbackResponse, INicheMap, IUserMap } from "@/common/services/types";

import { MobileContentPageScrollKeys } from "components/ads/config/units/contentPageScrollMobile";
import { DesktopContentPageScrollKeys } from "components/ads/config/units/contentPageScrollDesktop";
import { FeedControl } from "components/feed/FeedControl";
import { TileFeed } from "components/feed/TileFeed";

import "./GifList.scss";
import { PreviewFeed } from "@/components/player-feed/PreviewFeed";
import { TAddFeedback } from "components/gifPreview/Vote";
import { InjectionFeed } from "components/player-feed/InjectionFeed";
import { canShow as canShowScrollDispatcher, ScrollAdDispatcher } from "components/ads/ScrollAdDispatcher";
import {
  canShow as canShowStreamateDisptacher,
  StreamateCameraDispatcher
} from "components/ads/StreamateCameraDispatcher";

interface IProps {
  ordering?: boolean;
  orderingTop?: boolean;
  topics?: boolean;
  manage?: boolean;

  topic: string;
  order: string;
  tiles: boolean;
  verified?: boolean;
  gifs: IGif[];
  users: IUserMap;
  niches?: INicheMap;
  totalLength: number;
  hasMore: boolean;
  changeTopic?: (topic: string) => void;
  changeOrder: (order: string) => void;
  changeMode: (tiles: boolean) => void;
  changeVerified?: (verified: boolean) => void;
  fetchMore: () => void;
  userID?: string;
  isError?: boolean;
  isLoading?: boolean;
  className?: string;
  injector?: (pos: number) => JSX.Element;
  onSelect?: (ids: string[]) => void;
  isVerifyHidden?: boolean;
  // todo: remove later (when all tiles will be replaced)
  isVerticalTiles?: boolean;
  currentTags?: string[];
  nicheFeedbacks?: INicheFeedbackResponse[];
  addNicheFeedback?: TAddFeedback;
  tileFeedHeader?: ReactNode;
  renderTileControls?: (gif: IGif) => ReactNode;

  // Passed to the watch page to know where the user comes from.
  rel?: string;
}

export const GifList = (props: IProps) => {
  const { injector: parentInjector } = props;

  const injector = useCallback((pos: number): JSX.Element => {
    const children: ReactNode[] = [];

    if (props.tiles) {
      return <Fragment />;
    }

    if (parentInjector) {
      return parentInjector(pos);
    }

    if (canShowScrollDispatcher(pos)) {
      children.push(<ScrollAdDispatcher
        key={`ScrollAdDispatcher-${pos}`}
        position={pos + 1}
        mobileAds={MobileContentPageScrollKeys}
        desktopAds={DesktopContentPageScrollKeys}
      />);
    }

    if (canShowStreamateDisptacher(pos)) {
      children.push(<StreamateCameraDispatcher
        key={`StreamateCameraDispatcher-${pos}`}
        position={pos}
        tags={props.currentTags || props.gifs[0]?.tags || []}
      />);
    }

    return <Fragment>{children}</Fragment>;
  }, [parentInjector, props.gifs, props.tiles]);

  return (
    <div className={`gifList ${props.className ? props.className : ""}`}>
      <FeedControl
        order={props.order}
        ordering={props.ordering}
        orderingTop={props.orderingTop}
        tiles={props.tiles}
        verified={!!props.verified}
        topics={props.topics}
        topic={props.topic}
        changeOrder={props.changeOrder}
        changeMode={props.changeMode}
        changeVerified={props.changeVerified}
        changeTopic={props.changeTopic}
      />

      {props.isError && <p className="errorMessage">Error loading gifs, please refresh.</p>}

      {props.tiles && <TileFeed
        gifs={props.gifs}
        hasMore={props.hasMore}
        fetchMore={props.fetchMore}
        manage={!!props.manage}
        onSelect={props.onSelect}
        injector={injector}
        rel={props.rel}
        order={props.order}
        verified={!!props.verified}
        isLoading={props.isLoading}
        isVerifyHidden={props.isVerifyHidden}
        userId={props.userID}
        niches={props.niches}
        users={props.users}
        header={props.tileFeedHeader}
        isVerticalTiles={props.isVerticalTiles}
        renderTileControls={props.renderTileControls}
      />}

      {!props.tiles && (
        <PreviewFeed>
          <Suspense>
            <InjectionFeed
              gifs={props.gifs}
              users={props.users}
              niches={props.niches}
              injector={injector}
              rel={props.rel}
              isLoading={props.isLoading}
              fetchMore={props.fetchMore}
              hasMore={props.hasMore}
              addNicheFeedback={props.addNicheFeedback}
              nicheFeedbacks={props.nicheFeedbacks}
            />
          </Suspense>
        </PreviewFeed>
      )}
    </div>
  );
};
